import React, { useState } from 'react';

// Third Party Libraries
import DatePicker from '~/components/General/OlcDatePicker';
import { addDays } from 'date-fns';

// Hooks
import { useDispatch } from 'react-redux';

// Actions
import { fetchApiKeys, rotateApiKey } from '~/redux/actions/apiKeys-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { dateProtector, handleRawChangeForDatePicker } from '~/utils/date-format';
import { DATE_REGEX } from '~/utils/constants';
import LimitReach from '~/components/General/LimitModal';

// MUI Components
import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Typography from '@mui/material/Typography';
import { Box, InputLabel, CircularProgress } from '@mui/material';
import IconButton from '@mui/material/IconButton';

// Icons
import { DetailsCrossCIcon, MultiSelectArrowCIcon } from '~/assets/images';

// styles
import './styles.scss';
import OlcButton from '~/components/General/OlcButton';
import OlcModalFooter from '~/components/General/OlcModalFooter';

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialogContent-root': {
    padding: theme.spacing(2),
  },
  '& .MuiDialogActions-root': {
    padding: theme.spacing(1),
  },
}));

const RotateApiModal = (props) => {
  const { selectedApi, openModal, handleCloseModal, pagination } = props;
  const [startDate, setStartDate] = useState(null);
  const [isDatepickerOpen, setIsDatepickerOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [limitModal, setLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');

  const dispatch = useDispatch();

  const onClose = () => {
    handleCloseModal();
    setStartDate(null);
  };

  const rotateApi = async () => {
    try {
      const payload = {
        expiredAt: startDate?.toISOString().slice(0, 10),
        isSecretRequired: false,
        selectedApi,
      };
      setLoading(true);
      const response = await dispatch(rotateApiKey(payload));
      if (response?.status === 418) {
        const errorMessage = response?.data?.message || MESSAGES.SETTINGS.API_KEYS.NOT_ALLOWED;
        onClose();
        setLimitMessage(errorMessage);
        setLimitModal(true);
        return;
      }
      if (!response?.data) {
        dispatch(failure(response ? MESSAGES.GENERAL_ERROR : MESSAGES.NO_RESPONSE_ERROR));
        return;
      }
      dispatch(fetchApiKeys(pagination));
      dispatch(success(response.data.message));
      onClose();
    } catch (error) {
      dispatch(failure(error?.response?.data?.message || error?.message || MESSAGES.GENERAL_ERROR));
      return error;
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  return (
    <>
      <LimitReach
        open={limitModal}
        onClose={() => {
          setLimitModal(false);
          setTimeout(() => setLimitMessage(''), 100);
        }}
        message={limitMessage}
        resourceType="api-keys"
      />
      <BootstrapDialog
        onClose={onClose}
        aria-labelledby="customized-dialog-title"
        open={openModal}
        className="olcModal createApiModalWrapper"
      >
        <DialogContent className="modalContent" sx={{ width: '636px' }}>
          <Box className="modalHeader">
            <Typography gutterBottom className="title">
              {MESSAGES.SETTINGS.API_KEYS.ROTATE.TITLE}
            </Typography>
            <IconButton
              aria-label="close"
              onClick={onClose}
              className="crossIcon"
              disableRipple
              disableFocusRipple
              disableTouchRipple
              sx={{
                '&:hover': {
                  backgroundColor: 'transparent',
                },
              }}
            >
              <img src={DetailsCrossCIcon} alt="close" style={{ width: '14px', height: '14px' }} />
            </IconButton>
          </Box>
          <Box className="updateFormWrapper apiKeyModalBody">
            {/* Expiry Date of API Key */}
            <Box className="mailedDate newDatePicker updatedDesign">
              <Box className="selectDate" sx={{ maxWidth: '100% !important' }}>
                <InputLabel className="dateLabel newfieldsLabel">
                  {MESSAGES.SETTINGS.API_KEYS.ROTATE.DATE_LABEL}
                </InputLabel>
                <img
                  src={MultiSelectArrowCIcon}
                  alt="arrow"
                  style={{
                    transform: isDatepickerOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  }}
                />
                <DatePicker
                  toggleCalendarOnIconClick={true}
                  selected={startDate}
                  minDate={addDays(new Date(), 1)}
                  onChange={(update) => {
                    dateProtector(update);
                    setStartDate(update);
                  }}
                  onChangeRaw={(e) => {
                    if (e.target.value === '' || DATE_REGEX.test(e.target.value)) {
                      handleRawChangeForDatePicker(e, setStartDate);
                    } else {
                      e.preventDefault();
                    }
                  }}
                  onFocus={() => setIsDatepickerOpen(true)}
                  onBlur={() => setIsDatepickerOpen(false)}
                  placeholderText={MESSAGES.SETTINGS.API_KEYS.ROTATE.DATE_PLACEHOLDER}
                />
              </Box>
            </Box>
          </Box>
          <OlcModalFooter className="modalActions">
            <OlcButton variant="primary" onClick={rotateApi} disabled={loading}>
              {loading ? (
                <CircularProgress
                  sx={{
                    color: 'white',
                    width: '25px !important',
                    height: '25px !important',
                  }}
                />
              ) : (
                MESSAGES.SETTINGS.API_KEYS.SUBMIT_BUTTON
              )}
            </OlcButton>
            <OlcButton variant="secondary" onClick={onClose}>
              {MESSAGES.SETTINGS.API_KEYS.CANCEL_BUTTON}
            </OlcButton>
          </OlcModalFooter>
        </DialogContent>
      </BootstrapDialog>
    </>
  );
};

export default RotateApiModal;
